import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Fund from '@/models/Fund';
import { FundStatus } from '@/models/common/FundStatus';
import { useGetAuthApi } from '@/hooks/UseApiCalls';
import { Field, inputClassName } from './dialogParts';

/** Funds registered here and active - a terminal is only handed to a fund that can work. */
const ACTIVE_FUNDS_URL = `funds/management?status=${FundStatus.Active}`;
const MAX_MATCHES = 8;

interface FundPickerFieldProps {
  id: string;
  label: string;
  value: number | null;
  onChange: (fundId: number | null) => void;
  /** The fund is given by the screen (the fund blade) and cannot be changed here. */
  locked?: boolean;
  /** The name to show for a given fund that is not in the active list (e.g. a frozen fund's blade). */
  fallbackName?: string;
}

/**
 * Search-and-pick of a fund, drawn inline. FundFilter's list opens in a portal on the body, which a
 * modal dialog makes unreachable (it blocks pointer events and keeps focus outside it), so inside a
 * dialog the list is part of the dialog itself.
 */
const FundPickerField = ({ id, label, value, onChange, locked = false, fallbackName }: FundPickerFieldProps) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const { data: funds, isLoading } = useGetAuthApi<Fund[]>(ACTIVE_FUNDS_URL);

  const selected = value != null ? (funds ?? []).find((f) => f.id === value) : null;

  const matches = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return [];
    return (funds ?? []).filter((f) => `${f.id} ${f.name}`.toLowerCase().includes(query)).slice(0, MAX_MATCHES);
  }, [funds, search]);

  if (value != null) {
    return (
      <Field id={id} label={label}>
        <div className="flex items-center justify-between gap-3 border border-(--border-main) rounded-lg px-3 py-2">
          <span id={id} className="body-sm-regular">
            {value} | {selected?.name ?? fallbackName ?? ''}
          </span>
          {!locked && (
            <button type="button" className="body-sm-semibold text-(--font-link)" onClick={() => { onChange(null); setSearch(''); }}>
              {t('paymentDevices.dialogs.changeFund')}
            </button>
          )}
        </div>
      </Field>
    );
  }

  return (
    <Field id={id} label={label}>
      <input
        id={id}
        type="text"
        className={inputClassName}
        value={search}
        placeholder={t('searchFund')}
        onChange={(e) => setSearch(e.target.value)}
        autoComplete="off"
      />
      {isLoading && search && <p className="body-sm-regular text-(--font-tertiary)">{t('loadingFunds')}...</p>}
      {!isLoading && search.trim() && matches.length === 0 && (
        <p className="body-sm-regular text-(--font-tertiary)">{t('noFundsFound')}</p>
      )}
      {matches.length > 0 && (
        <ul className="border border-(--border-main) rounded-lg max-h-[220px] overflow-y-auto custom-scrollbar">
          {matches.map((fund) => (
            <li key={fund.id}>
              <button
                type="button"
                className="w-full text-start px-3 py-2 body-sm-regular hover:bg-(--surface-secondary-lowest)"
                onClick={() => { onChange(fund.id); setSearch(''); }}
              >
                {fund.id} | {fund.name}
              </button>
            </li>
          ))}
        </ul>
      )}
    </Field>
  );
};

export default FundPickerField;
